import { FunctionComponent, useEffect, useState } from 'react';
import AttachmentUpload from 'src/shared/AttachmentUpload';
import { DescriptionInput } from './styled';
import {
    SectionTitle,
    SectionSubtitle,
    StartPlanningButton,
    OutlinedActionButton,
    DeleteDraftButton,
    StartButtonContainer,
    ModeFormControl,
    ModeLabel,
    ModeMenuItem,
    ModeSelect,
    RecommendedChip,
    StartProgress,
    modeMenuProps
} from '../../styled';

type PlanningMode = 'planning' | 'quick';

interface Props {
    onSubmit: (description: string, attachmentList?: number[]) => void;
    onQuickDelegate: (description: string, attachmentList?: number[]) => void;
    onSaveAsDraft: (description: string, attachmentList?: number[]) => void;
    onDeleteDraft?: () => void;
    isLoading: boolean;
    initialDescription?: string;
}

const PlanningInput: FunctionComponent<Props> = ({
    onSubmit,
    onQuickDelegate,
    onSaveAsDraft,
    onDeleteDraft,
    isLoading,
    initialDescription
}) => {
    const [description, setDescription] = useState(initialDescription || '');
    const [attachmentIds, setAttachmentIds] = useState<number[]>([]);
    const [mode, setMode] = useState<PlanningMode>('planning');

    useEffect(() => {
        setDescription(initialDescription ?? '');
    }, [initialDescription]);

    const isQuickMode = mode === 'quick';

    const handleSubmit = () => {
        if (!description.trim()) return;
        if (isQuickMode) {
            onQuickDelegate(description.trim(), attachmentIds);
        } else {
            onSubmit(description.trim(), attachmentIds);
        }
    };

    return (
        <>
            <SectionTitle>
                {isQuickMode ? 'Delegate a request' : 'Plan a new request'}
            </SectionTitle>
            <SectionSubtitle>
                {isQuickMode
                    ? 'Describe the feature or change you want built. The request will be created and delegated immediately.'
                    : 'Describe the feature or change you want built. Our AI will explore the codebase, ask clarifying questions, and produce an implementation plan.'}
            </SectionSubtitle>

            <ModeFormControl size="small">
                <ModeLabel>Mode</ModeLabel>
                <ModeSelect
                    value={mode}
                    onChange={(e) => setMode(e.target.value as PlanningMode)}
                    MenuProps={modeMenuProps}
                >
                    <ModeMenuItem value="planning">
                        Planning
                        <RecommendedChip>recommended</RecommendedChip>
                    </ModeMenuItem>
                    <ModeMenuItem value="quick">Quick</ModeMenuItem>
                </ModeSelect>
            </ModeFormControl>

            <DescriptionInput
                multiline
                minRows={6}
                maxRows={16}
                placeholder="Describe what you want to build..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
            />
            <AttachmentUpload
                attachmentIds={attachmentIds}
                setAttachmentIds={setAttachmentIds}
                compact
            />
            <StartButtonContainer>
                {onDeleteDraft && (
                    <DeleteDraftButton
                        variant="contained"
                        onClick={onDeleteDraft}
                        disabled={!description.trim() || isLoading}
                    >
                        Delete Draft
                    </DeleteDraftButton>
                )}
                <OutlinedActionButton
                    variant="contained"
                    onClick={() => {
                        if (!description.trim()) return;
                        onSaveAsDraft(description.trim(), attachmentIds);
                    }}
                    disabled={isLoading}
                    $dimmed={!description.trim()}
                >
                    Save as Draft
                </OutlinedActionButton>

                <StartPlanningButton
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!description.trim() || isLoading}
                >
                    {isLoading ? (
                        <StartProgress size={20} />
                    ) : isQuickMode ? (
                        'Delegate'
                    ) : (
                        'Start Planning'
                    )}
                </StartPlanningButton>
            </StartButtonContainer>
        </>
    );
};

export default PlanningInput;
